import { cn } from 'cn';
import remarkGfm from 'remark-gfm';
import urlcat from 'urlcat';

import { RELEASE_NOTES_ASSETS_BASE_URL } from '~/data/releaseNotes';

import { i18n } from '~/utils';

import { type HTMLElementChildren, Icon } from '@vestigas/ui';

import { remarkAlert } from './remarkAlert';
import { remarkGallery } from './remarkGallery';

/**
 * Configuration for the markdown renderer used specifically for the release notes.
 * Contains custom components for the markdown renderer.
 */
export const releaseNotesMarkdownConfig = {
  components: {
    a({ children, href }: { href: string; children: string }) {
      return (
        <a href={href} className="text-tiga-action-default">
          {children.replace(/^https?:\/\//, '')}
        </a>
      );
    },
    blockquote({
      children,
      ...props
    }: HTMLElementChildren & Record<string, unknown>) {
      // react-markdown passes hProperties as props
      const alertType =
        (props['data-alert-type'] as string | undefined) ??
        (props.dataAlertType as string | undefined);

      if (alertType) {
        const alertStyles: Record<string, string> = {
          info: 'bg-purple-50 border-purple-200 border-l-4',
          summary: '',
          warning: 'bg-orange-50 border-orange-200 border-l-4',
        };
        const alertTypeTranslationKey = {
          info: i18n.t(($) => $.alertType.info, {
            ns: 'release',
          }),
          summary: i18n.t(($) => $.alertType.summary, {
            ns: 'release',
          }),
          warning: i18n.t(($) => $.alertType.warning, {
            ns: 'release',
          }),
        };

        const styleClass = alertStyles[alertType];
        const translatedLabel = alertTypeTranslationKey[alertType] || alertType;

        return (
          <blockquote
            className={cn('space-y-1 rounded-sm text-sm', styleClass)}
          >
            <h3 className="flex items-center gap-2 text-lg font-semibold">
              <Icon metaphor="announce" /> {translatedLabel}
            </h3>
            {children}
          </blockquote>
        );
      }

      // Regular blockquote
      return (
        <blockquote className="border-l-4 border-tiga-border-lighter pl-2">
          {children}
        </blockquote>
      );
    },
    h1({ children }: HTMLElementChildren) {
      return (
        <h1 className="w-full text-4xl tracking-tight text-balance">
          {children}
        </h1>
      );
    },
    h2({ children }: HTMLElementChildren) {
      return (
        // <h2 className="absolute left-0 flex -translate-x-[calc(100%+1.5rem)] justify-end rounded-full border px-3 py-0.5 text-2xl font-semibold tracking-tight [&>em]:absolute [&>em]:translate-x-2 [&>em]:translate-y-full [&>em]:justify-end [&>em]:whitespace-nowrap [&>em]:pt-2 [&>em]:text-sm [&>em]:font-normal [&>em]:not-italic [&~*]:pt-1.5">
        <h2 className="flex items-baseline border-b border-tiga-border-default text-xl font-semibold tracking-tight [&>em]:ml-auto [&>em]:inline-flex [&>em]:text-sm [&>em]:whitespace-nowrap [&>em]:not-italic">
          {children}
        </h2>
      );
    },
    h3({ children }: HTMLElementChildren) {
      return (
        <h3 className="mt-2 w-full text-lg tracking-tight text-balance">
          {children}
        </h3>
      );
    },
    h4({ children }: HTMLElementChildren) {
      return (
        <h4 className="mt-4 w-full text-base tracking-tight text-balance">
          {children}
        </h4>
      );
    },
    img({ alt, src }: { alt: string; src: string }) {
      const imgUrl = src
        ? urlcat(`${RELEASE_NOTES_ASSETS_BASE_URL}/:src`, { src })
        : '';

      return (
        <figure className="max-w-2xl">
          <img
            alt={alt}
            src={imgUrl}
            className="rounded-sm border border-tiga-border-lighter bg-white p-2 shadow-md"
          />
          {alt && (
            <figcaption className="mt-1.5 px-2 text-sm leading-tight text-tiga-text-lighter">
              {alt}
            </figcaption>
          )}
        </figure>
      );
    },
    ol({ children }: HTMLElementChildren) {
      return <ol className="ml-4 list-decimal">{children}</ol>;
    },
    ul({ children, ...props }: HTMLElementChildren & Record<string, unknown>) {
      const isGallery =
        props['data-gallery'] === 'true' || props.dataGallery === 'true';

      if (isGallery) {
        return (
          <ul className="grid w-full list-none grid-cols-[repeat(auto-fit,minmax(max(12rem,calc(25%-0.75rem)),1fr))] gap-4">
            {children}
          </ul>
        );
      }

      return <ul className="ml-4 list-disc">{children}</ul>;
    },
  },
  remarkPlugins: [remarkGfm, remarkAlert, remarkGallery],
};
